import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Button, Modal } from 'react-bootstrap';
import { applySuggestion } from '../../../services/EpisodeScriptService';
import { ConfirmModal } from '../../ui/ConfirmModal/ConfirmModal';
import { AdvancedFooter } from './components/AdvancedFooter';
import {
  AdvicePanel,
  NEUTRAL_RULES,
  suggestionCount,
  SUGGESTIONS,
} from './components/AdvicePanel';
import { AutocompletePopover } from './components/AutocompletePopover';
import { ExplanationCard } from './components/ExplanationCard';
import { Introduction } from './components/Introduction';
import { MediaDetail } from './components/MediaDetail';
import { NameNotes } from './components/NameNotes';
import { PlanPanel } from './components/PlanPanel';
import type { PlanWarning } from './components/PlanPanel';
import type { NameGap } from './viewModel/publicNames';
import { PreviewPanel, previewRows } from './components/PreviewPanel';
import { ScriptEditor } from './components/ScriptEditor';
import type { TextMetrics } from './components/ScriptEditor';
import { StatusBar } from './components/StatusBar';
import { isVerifiedSuccess } from './write/applyOutcome';
import {
  applyingSentence,
  ATTESTATION_NOT_SAVED,
  checkingSentence,
  CONFIRM_APPLY,
  CONFIRM_NOT_YET,
  confirmQuestion,
  EARLIER_UNCONFIRMED,
  EXIT_UNCONFIRMED,
  nextUnsettled,
  outcomeOf,
  outcomeSentence,
  PREPARING_CHANGES,
  recoveryLines,
} from './viewModel/outcomes';
import { useApply } from './write/useApply';
import type { FrozenApplyArgs } from './write/useApply';
import type { ApplyState } from './write/writeTypes';
import { useAutocomplete } from './hooks/useAutocomplete';
import { useBasilDocument } from './hooks/useBasilDocument';
import { useCaretContext, sceneAt } from './hooks/useCaretContext';
import { useDebouncedAnalysis } from './hooks/useDebouncedAnalysis';
import { useEpisodeScript } from './read/useEpisodeScript';
import { useBookNames } from './read/useBookNames';
import { useBookMedia } from './read/useBookMedia';
import { useVerifyClient } from './hooks/useVerifyClient';
import {
  branchAtLine, graphOfFixture, sceneHeadings, sceneOfBranch,
} from './viewModel/compactGraph';
import { backgroundIdsOf, mediaLabelsByLine } from './viewModel/mediaLabels';
import { exampleTarget, isSpeakerLabel, scriptForms } from './viewModel/example';
import { alignLines } from './viewModel/lineAlignment';
import { heightBudgetStyle } from './viewModel/heightBudget';
import { readIntroductionDone, writeIntroductionDone } from './viewModel/firstRun';
import { humanise, publicNames, variableWord } from './viewModel/publicNames';
import { SETTLE_FIRST } from './viewModel/refusals';
import css from './BasilModal.module.scss';
import type {
  BasilStatusState,
  BasilModalProps,
  BasilSendability,
  CaretPoint,
} from './types/basilTypes';
import { BASIL_REFUSAL_COPY, BASIL_TOAST_MS, EMPTY_SENDABILITY } from './types/basilTypes';
import type { ApplyOutcome } from './viewModel/outcomes';
import type {
  Branch,
  IdentifiedFinding,
  Plan,
  SuppliedNames,
} from '../../../dorian-shared/types/episodeScript';

/** The hard ceiling on a step's or an answer's text, whatever the settings say (economy.ts:18). */
const TEXT_COLUMN_LIMIT = 255;

const LISTBOX_ID = 'basil-complete-list';

/** One stable empty plan set, so the write lane's memo does not churn on every render. */
const NO_PLANS: Plan[] = [];

/** The same, for the findings: a fresh `[]` every render would re-filter the suggestions forever. */
const NO_FINDINGS: IdentifiedFinding[] = [];

/** Shown while the analysis the writer is looking at is not the analysis of what they typed. */
const WORKING_ON_THE_LATEST_EDIT = 'Reading your latest edit…';

/** Shown when the script itself could not be turned into a plan at all. */
const ANALYSIS_FAILED = "Basil can't quite follow the script yet, so nothing will change until it can.";

/** The plain fallback when an apply ended without a refusal of its own. */
const APPLY_NOT_APPLIED = "Basil couldn't confirm all your changes. The Changes list shows where each one stands.";

/**
 * The sentence the PIPELINE itself wrote about this attempt, if it wrote one (Basil v0.2, P5).
 *
 * `BASIL_REFUSAL_COPY` is keyed by the refusal CODE, so it can only ever say the general shape of a
 * refusal. The pipeline files the actual sentence — the matching-answer case, the episode that
 * changed elsewhere, the browser that cannot keep a record — as the last of `state.details`. Only
 * the refusal that applies is shown, so that sentence leads and the code's copy is the fallback
 * behind it.
 */
function refusalSaid(state: { details?: Array<{ message: string }> } | null): string | null {
  const details = state && state.details ? state.details : [];
  const last = details.length > 0 ? details[details.length - 1] : null;

  return last && last.message ? last.message : null;
}

/**
 * Beside Apply for as long as a confirmed batch is out (BAR-SHIP-14, J1).
 *
 * The interval runs from the confirmation to the pipeline's final state, and for the whole of it the
 * script is locked: a line typed against a batch already in flight was never part of that batch, and
 * the batch's own success would close the editor over it.
 */
const APPLY_IN_FLIGHT = 'Putting your changes into the episode… The script unlocks in a moment.';

/** What Close is told while a batch is out. Nothing leaves the editor until the batch has an outcome. */
const APPLY_WAIT_TO_CLOSE = 'Almost there. Give it a moment to finish.';

/**
 * Said when a batch came back good but the script is no longer the revision that was confirmed.
 *
 * Under the in-flight lock this cannot be reached by typing; the revision binding is the second wall
 * and it decides, so the sentence exists for every path that ever gets past the first one.
 */
const NEWER_EDIT_KEPT = 'Done. The edit you typed after pressing Apply is still here in the script, '
  + "not in the episode yet. Apply again when you're ready.";

/** The two keys the surface is driven by, said once, beside the actions they belong to. */
const KEY_HINT = 'Ctrl+Enter to apply · Esc to close';

/**
 * The PROTECTED EXIT (Basil v0.2, P3).
 *
 * Every way out of Basil with unapplied edits in the editor comes through one question, and every
 * one of them asks it before anything is lost: Close, a media label's action, the "Open this scene"
 * on a refusal, and a row of the Scenes panel. Nothing closes silently.
 *
 * The two answers are named for what they DO. "Stay" said nothing about what staying was for;
 * "Close without saving" said nothing about what would still be in the episode afterwards.
 */
const KEEP_WRITING = 'Keep writing';

/** The confirming answer, with the scene it opens, or the plain close where no scene is named. */
const discardAndOpen = (sceneName: string | null): string => (sceneName
  ? `Discard unapplied edits and open ${sceneName}`
  : 'Discard unapplied edits and close');

/** The question itself, and the promise that makes it safe to answer either way. */
const DIRTY_EXIT_TITLE = 'You have unapplied edits';
const DIRTY_EXIT_BODY = 'The edits you have not applied will be discarded. '
  + 'Changes Basil has already applied stay in your episode.';

/** The confirmation's own heading. The question under it carries the count and the consequence. */
const APPLY_CONFIRM_TITLE = 'Apply changes';

/** P3, said once: what a label's action actually does when it gets there. */
const EXIT_EXPLANATION = 'Opening a scene takes you back to that scene and its Edit control on the story view.';

/**
 * T17-06: what the list outside the fold is called.
 *
 * "Suggestions" is the right word for something a writer may take or leave. A blocking problem is
 * not that, and putting it under that heading — inside a fold that is closed on open — told a
 * writer they could ignore it and then hid it from them anyway.
 */
const NEEDS_ATTENTION = 'Needs attention';

/**
 * T18-01: the one control that settles an earlier unknown, in the writer's own words.
 *
 * It is not "Dismiss": a warning about the episode is not something to tidy away, and the only
 * reason to take it down is that the writer went and looked. The control says what pressing it
 * means, and pressing it is the writer's statement, not Basil's inference.
 */
const CHECKED_THE_EPISODE = 'I have checked the episode';

/**
 * P6 and T18-03: the width the two columns were drawn for.
 *
 * Under it the rail goes below the script and shows ONE panel at a time, so two of the three side
 * panels are out of the document's flow. The stylesheet lays the surface out from this same query,
 * and the surface puts the answer in the document as `data-narrow` on the shell: the condition that
 * hides a panel is then a fact anyone — a writer's screen, a test — can read, rather than something
 * only a rendering engine knows.
 */
const NARROW_LAYOUT = '(max-width: 1080px)';

/** True while the window is under the width the two columns were drawn for. */
function useNarrowLayout(): boolean {
  const [narrow, setNarrow] = useState(false);

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') {
      return undefined;
    }
    const query = window.matchMedia(NARROW_LAYOUT);
    const read = (): void => setNarrow(Boolean(query.matches));

    read();

    if (typeof query.addEventListener === 'function') {
      query.addEventListener('change', read);

      return () => query.removeEventListener('change', read);
    }
    if (typeof query.addListener === 'function') {
      // Safari before 14, and jsdom's own media query list.
      query.addListener(read);

      return () => query.removeListener(read);
    }

    return undefined;
  }, []);

  return narrow;
}

/** Where the context rail opens, and how far the handle between the columns may take it, in pixels. */
const RAIL_DEFAULT = 360;
const RAIL_MIN = 280;
const RAIL_MAX = 720;

/**
 * P6: the three side panels, in the order the brief names them.
 *
 * They are tabs only under the width the two columns were drawn for; above it all three stack, as
 * they always did. The words are the panels' own headings and nothing else.
 */
const RAIL_TABS: ReadonlyArray<{ id: 'changes' | 'scenes' | 'suggestions'; word: string }> = Object.freeze([
  { id: 'changes', word: 'Changes' },
  { id: 'scenes', word: 'Scenes' },
  { id: 'suggestions', word: 'Suggestions' },
]);

/** One arrow key's worth of rail. */
const RAIL_STEP = 24;

/** The rail never leaves the range the two columns were drawn for, however it is asked to. */
const clampRail = (width: number): number => Math.min(RAIL_MAX, Math.max(RAIL_MIN, Math.round(width)));

/** How the top bar says a script cannot go as one piece. */
function sendabilitySentence(sendability: BasilSendability): string | null {
  if (sendability.total === 0 || sendability.blocked === 0) {
    return null;
  }
  // A planner refusal opens with its own 'Nothing changed.'; nested here, the sentence already says so.
  const reason = (sendability.firstBlockingMessage ?? '').replace(/^Nothing changed\. /, '');
  // P5: "needs a look first" invited the writer to look for an approval control that does not
  // exist. The bar says the same fact the held row does — this one cannot be sent here — and the
  // row's own sentence, which carries the action, follows the colon.
  if (sendability.total === 1) {
    return `Your change can't be sent yet: ${reason}`;
  }

  // Choice 22, corrected by the review seat: the verb agrees with the COUNT that is ready, not with
  // the word "changes" beside it. "1 of your 2 changes are ready" is the sentence a writer stops at.
  const ready = `${sendability.sendable} of your ${sendability.total} changes `
    + `${sendability.sendable === 1 ? 'is' : 'are'} ready.`;
  const held = `${sendability.blocked} cannot be sent yet: ${reason}`;

  return `${ready} ${held}`;
}

/** Which domain an id's letter names. `V` never reaches this: variables are counted, not named. */
const domainOf = (letter: string): 'characters' | 'expressions' | 'locations' => {
  if (letter === 'C') {
    return 'characters';
  }
  if (letter === 'E') {
    return 'expressions';
  }

  return 'locations';
};

/** Two caret boxes are the same box when every number in them is. */
const samePoint = (a: CaretPoint | null, b: CaretPoint | null): boolean => {
  if (!a || !b) {
    return a === b;
  }

  return a.left === b.left && a.top === b.top && a.bottom === b.bottom
    && a.box.top === b.box.top && a.box.bottom === b.box.bottom && a.box.left === b.box.left;
};

/**
 * The episode's ACTUAL title, for the bar (Basil v0.2, P1).
 *
 * The page holds it: `story.title` is the name the writer gave this episode. The decompiled script
 * carries no title line of its own, so a surface reading only the script could say nothing but
 * "Episode" — which is what it used to say, on every episode in the book. The script's own `# `
 * line is still honoured where one exists, and the chapter number is the last thing left to say.
 */
const titleOf = (script: string, story?: { title?: string; chapter?: number } | null): string => {
  const given = story && typeof story.title === 'string' ? story.title.trim() : '';

  if (given !== '') {
    return given;
  }
  const matched = script.match(/^#\s+(.+)$/m);

  if (matched) {
    return matched[1];
  }
  if (story && story.chapter != null) {
    return `Episode ${story.chapter}`;
  }

  return 'Episode';
};

const linesOf = (script: string): string[] => String(script ?? '').replace(/\r\n?/g, '\n').split('\n');

/**
 * Basil — the writing surface on the Story Branches page.
 *
 * It reads the episode, decompiles it to a script, and gives the writer one text layer to edit with
 * soft wrap, a gutter that marks the lines the compiler asked about, advice cards that never write,
 * a projection preview, and a plan panel that says what would be sent. Everything the engine needs to
 * say about a change — the op, the ids, the request body — is inside the collapsed Advanced drawer
 * and nowhere else.
 *
 * The surface is FULL-BLEED and laid out once:
 *
 * ```
 * ┌──────────────────────────────────────────────────────────────────────────────┐
 * │ Basil │ title · count · status · Context │ why not · keys │ Apply │ Close     │  header
 * ├────────────────────────────────────────────────┬─┬───────────────────────────┤
 * │ legend                                         │ │ PLAN     (leads when       │
 * │ ┌────┬────────────────────────────────────────┐│ │           anything is      │
 * │ │ 12 │ the script — the only scroller here    ││ │           staged)          │
 * │ │  ? │                                        ││◄►│ ADVICE                    │
 * │ └────┴────────────────────────────────────────┘│ │ PREVIEW                    │
 * │ Advanced ▸ (a drawer, at most two fifths high) │ │ each with its own scroll   │
 * └────────────────────────────────────────────────┴─┴───────────────────────────┘
 * ```
 *
 * The header never scrolls, because it is outside every scroller; the script pane and the rail take
 * the rest of the viewport between them and scroll inside themselves; the Advanced drawer shortens
 * the editor and never covers it. The handle between the columns is pointer events and two arrow
 * keys, and the Context toggle folds the rail away and gives the script the whole width.
 *
 * The pipeline is `read → decompile → text`, then, 400 ms after the last keystroke,
 * `parse → compile → structureReport → analyse → projection → plan`. Everything caret-driven is
 * synchronous and not debounced. The four text layers — the gutter rows, the syntax overlay, the
 * textarea and the caret mirror — share one font and wrap rule, which is what keeps the numbers, the
 * colours and the caret on the same rows.
 *
 * Nothing here writes to the engine on its own: the write path is the write lane's, it is gated by
 * `canBasilWrite`, and it refuses unless the verifier answered first.
 */
export function BasilModal(props: BasilModalProps) {
  const {
    show,
    episodeId,
    bookId,
    preloadedNodes,
    memoryBank,
    limits,
    isReadOnly = false,
    initialBranchId,
    story,
    authUser,
    onNavigateToNode,
    onSave,
    onNotice,
    onHide,
  } = props;
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const mirrorRef = useRef<HTMLDivElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const metricsRef = useRef<TextMetrics>({ padTop: 18, lineHeight: 24 });

  const [contextOpen, setContextOpen] = useState(true);
  /** The rail's own width, dragged by the handle between the columns and nudged by the arrow keys. */
  const [railWidth, setRailWidth] = useState(RAIL_DEFAULT);
  /** Where a drag began and how wide the rail was then; null while nothing is being dragged. */
  const railDragRef = useRef<{ pointerX: number; width: number } | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  /**
   * The scene the writer asked the preview for while the script was dirty (BAR-SHIP2-04).
   *
   * Preview navigation is a way out of the editor: the mount hides the modal and scrolls the canvas
   * to the node. So the request is HELD here while the dirty-close confirmation stands, and it is
   * carried out only by that dialog's own discard path. Null whenever the confirmation is about a
   * plain Close, so a stale request can never turn a Close into a navigation.
   */
  const [pendingNavigateId, setPendingNavigateId] = useState<number | null>(null);
  /** P3: the scene that navigation would open, so the question can name it. */
  const [pendingNavigateName, setPendingNavigateName] = useState<string | null>(null);
  const [showApplyConfirm, setShowApplyConfirm] = useState(false);
  /**
   * P1: the line "Show an example" is pointing at, and whether the card beside it is open.
   *
   * The card closes only when the writer dismisses it. The line stays lit for exactly as long, so
   * looking away from the card and back at the script finds the same line waiting.
   */
  const [exampleLine, setExampleLine] = useState<number | null>(null);
  const [exampleOpen, setExampleOpen] = useState(false);
  /**
   * T19-04: the 1-based script line whose labels the writer asked to see in full, or null.
   *
   * The margin can only ever show a line's worth of a line's labels, because every row of it is as
   * tall as the script line beside it. This is the writer asking for the rest, and it is kept here
   * rather than in the margin because the panel that answers it is not in the margin.
   */
  const [mediaDetailLine, setMediaDetailLine] = useState<number | null>(null);
  /**
   * P1: false until this browser has recorded a confirmed apply for this episode.
   *
   * Read once, at the first render, and written on the first confirmed apply. Fail-open: a browser
   * that cannot answer keeps the instruction up, which is the side that costs a writer nothing.
   */
  const [introductionDone, setIntroductionDone] = useState<boolean>(() => readIntroductionDone(episodeId));
  /** P6: Suggestions waits to be asked for. It opens on the writer's word and stays open after. */
  const [suggestionsOpen, setSuggestionsOpen] = useState(false);
  /**
   * P6: which side panel a narrow window is showing.
   *
   * The state always exists; only a narrow window acts on it, and it does so in CSS. That keeps one
   * rail in the document at every width — a panel a writer cannot see is still a panel the surface
   * can put an outcome into.
   */
  const [railTab, setRailTab] = useState<'changes' | 'scenes' | 'suggestions'>('changes');
  /** T18-03: whether the rail is showing one panel at a time, as the stylesheet has it. */
  const narrowLayout = useNarrowLayout();
  const [popoverContainer, setPopoverContainer] = useState<Element | null>(null);
  const [caretPoint, setCaretPoint] = useState<CaretPoint | null>(null);
  // The caret box the surface last painted. Measuring is cheap and happens on every scroll, every
  // resize and every keystroke; setting state for a box that has not moved would ask React to render
  // in a loop, so a measurement is compared against the last one before it is stored.
  const caretPointRef = useRef<CaretPoint | null>(null);
  /**
   * True from the confirmation click until the pipeline's final state, whatever that state is
   * (BAR-SHIP-14, J1). It is set before the apply promise is started and cleared on every outcome —
   * a verified success, a refusal, and a boundary that throws.
   */
  const [applyInFlight, setApplyInFlight] = useState(false);

  /**
   * Every editing entry point reads this one value: the textarea's `readOnly`, the change handler,
   * the autocomplete, and the advice cards' mechanical fixes. A read-only episode and a batch in
   * flight lock the same set of doors, so there is no arrangement of state in which one of them is
   * shut and another is open.
   */
  const editingLocked = isReadOnly || applyInFlight;

  // The verifier is wired FIRST, because both the read and the write need it: the read asks it for
  // the staleness baseline of the script it is about to decompile, and the write refuses until its
  // handshake has answered.
  const verifier = useVerifyClient(show);

  const read = useEpisodeScript(episodeId, {
    enabled: show,
    preloadedNodes: preloadedNodes as unknown as Branch[] | undefined,
    verifyClient: verifier.client,
  });

  // The book's own words for its characters, moods and locations, read once per open beside the
  // episode read (Basil v0.1.1). Fail-open: a list that cannot be read supplies nothing and that
  // domain keeps its ids, exactly as the script read before.
  const bookNames = useBookNames(bookId, { enabled: show });

  // P3: which backgrounds this episode's own locations use. It is empty until the locations read
  // has answered, and the label read waits for it rather than guessing.
  const backgroundIds = useMemo(
    () => backgroundIdsOf(read.fixture, bookNames.names.locationMedia),
    [read.fixture, bookNames.names.locationMedia],
  );
  // The titles behind the margin labels, read once per open beside the episode read. Fail-open on
  // both halves, and a read that FAILED is remembered as failed: the margin says "details
  // unavailable" rather than showing an attachment the writer has as an absence.
  const bookMedia = useBookMedia(bookId, { enabled: show, backgroundIds });

  // What the script is allowed to print instead of an id: the memory bank's word for a variable
  // (spec §10.1 K8), and now the book's word for a character, a mood and a location.
  const supplied = useMemo<SuppliedNames>(() => {
    const variables: Record<string, string> = {};
    (memoryBank ?? []).forEach((memory) => {
      if (memory && memory.id != null) {
        variables[String(memory.id)] = memory.displayName || memory.name;
      }
    });

    return {
      variables,
      characters: bookNames.names.characters,
      expressions: bookNames.names.expressions,
      locations: bookNames.names.locations,
    };
  }, [memoryBank, bookNames.names]);

  // ORDERING. The three lists are asked for at open, beside the episode read, so the first
  // decompile normally already has the words: three small lists answer sooner than a full branches
  // read does. When they land second the document re-decompiles with them, and that is SAFE
  // because `useBasilDocument` binds its reset to the READ and not to the words: a writer who has
  // already typed keeps every character of it, and the sidecar map the compiler aligns on does not
  // depend on the words at all. The script is never held back waiting for a name.
  const basilDocument = useBasilDocument(read.fixture, supplied);
  const {
    text,
    baseline,
    map,
    names,
    setText,
    isDirty,
  } = basilDocument;
  /**
   * T17-04: where each of the sidecar map's lines sits in the script as it stands.
   *
   * Everything keyed by a map line — the media labels, the scene headings, the example's target,
   * which scene a line belongs to — is rendered against the CURRENT text, and the two stop agreeing
   * the moment the writer adds or removes a line. One blank line at the top used to leave the first
   * scene's labels on the blank line and move every later label one row out.
   */
  const alignment = useMemo(() => alignLines(baseline, text), [baseline, text]);
  // The script as it stands NOW, readable from inside a resolved apply. The close decision compares
  // this against the revision the confirmation was made from, and a closure over a render's `text`
  // would be comparing the batch against itself.
  const textRef = useRef(text);

  textRef.current = text;
  const {
    analysis,
    isAnalysing,
    errorMessage: analysisError,
  } = useDebouncedAnalysis(text, map, read.fixture, names);
  const caret = useCaretContext(textareaRef);

  // Apply is bound to the revision that was analysed. An edit invalidates sendability on the very
  // render it happens: `analysedText` is the exact text the plans were built from, so a keystroke
  // makes this false synchronously, well before the 400 ms debounce has produced anything new.
  const analysisIsCurrent = analysis != null
    && !isAnalysing
    && analysisError == null
    && analysis.analysedText === text;
  // The exact revision a confirmation opened now would be about. It is the analysed text where the
  // analysis is current, and the typed text otherwise — never null, so the close binding always has
  // something to compare and fails closed rather than open.
  const analysedRevisionRef = useRef(text);

  analysedRevisionRef.current = analysisIsCurrent && analysis ? analysis.analysedText : text;
  const sendability = analysisIsCurrent && analysis ? analysis.sendability : EMPTY_SENDABILITY;
  // A script goes whole or not at all: one blocked row disables Apply for all of them.
  const everyRowSendable = sendability.total > 0 && sendability.blocked === 0;
  const plansForWrite = useMemo<Plan[]>(
    () => (analysis && everyRowSendable ? analysis.plans : NO_PLANS),
    [analysis, everyRowSendable],
  );

  const lines = useMemo(() => linesOf(text), [text]);
  const flatNames = useMemo(() => publicNames(names), [names]);
  const graph = useMemo(() => graphOfFixture(analysis?.compiled?.fixture ?? read.fixture), [analysis, read.fixture]);
  const headings = useMemo(() => sceneHeadings(map, text, alignment), [alignment, map, text]);
  // Read through refs by the exit handlers, so naming the scene in the question costs those
  // callbacks none of the stable identity the in-flight lock and the preview rows depend on.
  const headingsRef = useRef(headings);
  const graphRef = useRef(graph);

  headingsRef.current = headings;
  graphRef.current = graph;

  /**
   * P3: the episode's pictures, video and sound, as read-only labels keyed by line.
   *
   * It is built from the READ fixture, never from the compiled one: the labels state what the
   * episode HAS, not what the script the writer is typing would make of it. Nothing here reaches
   * the textarea's value, the analysis or the write path.
   */
  const mediaLabels = useMemo(() => mediaLabelsByLine({
    map,
    fixture: read.fixture,
    locationNames: bookNames.names.locations,
    locationMedia: bookNames.names.locationMedia,
    locationsRead: bookNames.names.locationsRead,
    audio: bookMedia.audio,
    backgrounds: bookMedia.backgrounds,
    headings,
    graph,
    align: alignment,
  }), [alignment, bookMedia, bookNames.names, graph, headings, map, read.fixture]);

  // The ceiling the client enforces itself: the server does not bound step or answer text and the
  // column is a VARCHAR(255), so a longer line would be truncated in silence.
  const textCeiling = useMemo(() => {
    const configured = Number(limits?.answer_text_max?.value ?? TEXT_COLUMN_LIMIT);

    return Math.min(TEXT_COLUMN_LIMIT, Number.isFinite(configured) && configured > 0 ? configured : TEXT_COLUMN_LIMIT);
  }, [limits]);

  const markedLines = useMemo(() => {
    const marked = new Set<number>(analysis ? Array.from(analysis.markedLines) : []);
    lines.forEach((line, index) => {
      if (line.length > textCeiling) {
        marked.add(index + 1);
      }
    });

    return marked as ReadonlySet<number>;
  }, [analysis, lines, textCeiling]);

  /**
   * No id reaches a writer: an unresolved character, mood or location becomes a margin note, and
   * every unnamed variable collapses into one closed note that counts them all.
   *
   * Basil v0.2, P2: the note is now gathered PER LINE rather than per script. It matches the thing
   * it is about — character, mood or location — and it carries the line and the scene it was found
   * in, which is what lets it offer the protected exit into that scene. A domain missing twice in
   * the same scene is one note, not two.
   */
  const nameGaps = useMemo(() => {
    const notes: NameGap[] = [];
    const seen = new Set<string>();
    const unnamed = new Set<string>();
    const scriptLines = linesOf(text);

    // T17-05: a character, mood or location is named in a STRUCTURAL LABEL — a scene heading or a
    // speaker line — and nowhere else. Scanning every line for an identifier-shaped token read a
    // word in the writer's own narration as a missing character and put a note about it in the
    // panel. The sidecar map says which lines are labels; only those are asked.
    const labelLines = new Set<number>();

    (map?.blocks ?? []).forEach((block) => {
      const at = alignment.current(Number(block.line));

      if (at == null) {
        return;
      }
      if (block.kind === 'scene') {
        labelLines.add(at);

        return;
      }
      if (block.kind === 'character' && isSpeakerLabel(scriptLines[at - 1] ?? '')) {
        labelLines.add(at);
      }
    });

    scriptLines.forEach((line, index) => {
      const number = index + 1;

      // A variable is spelled wherever the writer put it, so it is still counted over the whole
      // script; it collapses into one closed note and names nothing on its own.
      (line.match(/\bV-?\d+\b/g) ?? []).forEach((token) => {
        const word = variableWord(flatNames, token);

        if (!word.known && word.id) {
          unnamed.add(word.id);
        }
      });

      if (!labelLines.has(number)) {
        return;
      }
      (line.match(/\b[CEL]-?\d+\b/g) ?? []).forEach((token) => {
        const domain = domainOf(token.charAt(0));
        const human = humanise(flatNames, domain, token);

        if (!human.question) {
          return;
        }
        const branchId = branchAtLine(map, number, alignment);
        const key = `${domain}#${String(branchId)}`;

        if (seen.has(key)) {
          return;
        }
        seen.add(key);
        notes.push({
          domain,
          note: human.question,
          line: number,
          branchId,
          sceneName: sceneOfBranch(headings, graph, branchId),
        });
      });
    });

    return { notes, unnamed: Array.from(unnamed) };
  }, [alignment, flatNames, graph, headings, map, text]);

  // The write lane owns every refusal. The surface hands it the page's own story and user, the
  // plans the debounced analysis produced, the read the script was decompiled from and the
  // verifier's own canon of that read — the staleness baseline, which the guard refuses without.
  // When the mount passes neither story nor user the gate refuses, which is the fail-closed default.
  //
  // The plans are handed over ONLY when every change row is sendable. Handing over the surviving
  // subset is how a mixed script would have applied half of itself while the top bar claimed the
  // whole count; there is now no arrangement of state in which a subset can reach the write lane.
  const apply = useApply({
    episodeId,
    bookId,
    story: story ?? null,
    user: authUser ?? null,
    plans: plansForWrite,
    before: read.snapshot,
    baselineCanon: read.canon,
    verifyClient: verifier.client,
    verifierAvailable: verifier.available,
    isReadOnly,
  });

  const handleTextChange = useCallback((next: string) => {
    setText(next);
  }, [setText]);

  const measureCaret = useCallback(() => {
    const node = textareaRef.current;
    const mirror = mirrorRef.current;
    if (!node || !mirror || typeof node.getBoundingClientRect !== 'function') {
      return;
    }
    const { value } = node;
    const at = node.selectionStart ?? 0;
    const start = value.lastIndexOf('\n', at - 1) + 1;
    mirror.textContent = value.slice(start, at);
    const marker = window.document.createElement('span');
    marker.textContent = String.fromCharCode(0x200b);
    mirror.appendChild(marker);
    const left = marker.offsetLeft;
    const withinLine = marker.offsetTop - metricsRef.current.padTop;
    mirror.textContent = '';
    const box = node.getBoundingClientRect();
    const lineIndex = value.slice(0, start).split('\n').length - 1;
    const top = box.top + metricsRef.current.padTop
      + lineIndex * metricsRef.current.lineHeight + withinLine - node.scrollTop;
    const next: CaretPoint = {
      left: box.left + left,
      top,
      bottom: top + metricsRef.current.lineHeight,
      box: { top: box.top, bottom: box.bottom, left: box.left },
    };

    if (samePoint(caretPointRef.current, next)) {
      return;
    }
    caretPointRef.current = next;
    setCaretPoint(next);
  }, []);

  /** The metrics sink is a stable identity: the editor re-measures on it, so it must not change. */
  const handleMetrics = useCallback((next: TextMetrics) => {
    metricsRef.current = next;
  }, []);

  const autocomplete = useAutocomplete({
    textareaRef,
    names,
    setText,
    onAfterAccept: () => {
      caret.refresh();
      measureCaret();
    },
    disabled: editingLocked,
  });

  // The textarea's own `readOnly` is the door a writer meets; this is the door the code meets. A
  // change event that arrives while the script is locked — from a paste, a synthetic event, an
  // extension — writes nothing, and React restores the textarea to the text state still holds.
  const handleChange = useCallback((next: string) => {
    if (editingLocked) {
      return;
    }
    handleTextChange(next);
    caret.refresh();
    measureCaret();
    autocomplete.refresh(true);
  }, [autocomplete, caret, editingLocked, handleTextChange, measureCaret]);

  const handleCaretMove = useCallback(() => {
    caret.refresh();
    measureCaret();
  }, [caret, measureCaret]);

  const handleGoToLine = useCallback((line: number) => {
    autocomplete.close();
    caret.goToLine(line);
  }, [autocomplete, caret]);

  /**
   * "Show an example", and "How this works" once the introduction has folded (P1).
   *
   * It scrolls to the episode's own first dialogue line — its first narration line when no line has
   * a speaker — lights it, and opens the card beside it. NOTHING is inserted: the example is a line
   * the writer already has, and an episode with no such line still gets the card.
   */
  const showExample = useCallback(() => {
    const target = exampleTarget(map, text, alignment);

    if (target) {
      autocomplete.close();
      caret.goToLine(target.line);
      setExampleLine(target.line);
    } else {
      setExampleLine(null);
    }
    setExampleOpen(true);
  }, [alignment, autocomplete, caret, map, text]);

  /** The card closes on the writer's word and on nothing else; the highlight goes out with it. */
  const dismissExample = useCallback(() => {
    setExampleOpen(false);
    setExampleLine(null);
  }, []);

  /** The four line forms, spelled with this episode's own words (P2). */
  const forms = useMemo(() => scriptForms(text), [text]);

  /**
   * The handle between the script and the rail: pointer events only, no library.
   *
   * The pointer is captured on the handle itself, so a fast drag that leaves the seven pixels keeps
   * resizing rather than dropping the gesture over the textarea. The arrow keys move it too, which
   * is the whole of the keyboard contract a separator owes.
   */
  const startRailResize = useCallback((event: React.PointerEvent<HTMLButtonElement>) => {
    railDragRef.current = { pointerX: event.clientX, width: railWidth };
    const node = event.currentTarget;

    if (typeof node.setPointerCapture === 'function') {
      node.setPointerCapture(event.pointerId);
    }
  }, [railWidth]);

  const moveRailResize = useCallback((event: React.PointerEvent<HTMLButtonElement>) => {
    const drag = railDragRef.current;

    if (!drag) {
      return;
    }
    setRailWidth(clampRail(drag.width - (event.clientX - drag.pointerX)));
  }, []);

  const endRailResize = useCallback((event: React.PointerEvent<HTMLButtonElement>) => {
    if (!railDragRef.current) {
      return;
    }
    railDragRef.current = null;
    const node = event.currentTarget;

    if (typeof node.hasPointerCapture === 'function' && node.hasPointerCapture(event.pointerId)) {
      node.releasePointerCapture(event.pointerId);
    }
  }, []);

  const nudgeRail = useCallback((event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') {
      return;
    }
    event.preventDefault();
    setRailWidth((width) => clampRail(width + (event.key === 'ArrowLeft' ? RAIL_STEP : -RAIL_STEP)));
  }, []);

  const showToast = useCallback((message: string) => {
    setToast(message);
  }, []);

  useEffect(() => {
    if (!toast) {
      return undefined;
    }
    const timer = setTimeout(() => setToast(null), BASIL_TOAST_MS);

    return () => clearTimeout(timer);
  }, [toast]);

  /**
   * Apply a mechanical fix. It travels by findingId; the returned note is always reported.
   *
   * It is an editing entry point like any other, so it is shut while a batch is out: an advice card
   * that rewrote a line under a batch already in flight would produce exactly the newer unsent
   * revision the lock exists to prevent.
   */
  const handleApplySuggestion = useCallback((findingId: string) => {
    if (editingLocked) {
      showToast(APPLY_IN_FLIGHT);

      return;
    }
    const result = applySuggestion({ script: text, findingId });
    if (result.applied) {
      setText(result.script);
    }
    showToast(result.note);
  }, [editingLocked, setText, showToast, text]);

  // The opening state: the caret on the first character and the script not scrolled, BEFORE any
  // context is derived — the fix for the defect where the drawers opened on a line the writer was
  // nowhere near (editor.js:939-941).
  const openedRef = useRef(false);
  useEffect(() => {
    if (!show) {
      openedRef.current = false;

      return;
    }
    if (openedRef.current || !text) {
      return;
    }
    openedRef.current = true;
    const node = textareaRef.current;
    if (node) {
      node.focus({ preventScroll: true });
      node.setSelectionRange(0, 0);
      node.scrollTop = 0;
    }
    caret.refresh();
    // The neighbour's entry point: open on the scene the canvas was looking at.
    if (initialBranchId != null) {
      const block = (map?.blocks ?? []).find(
        (one) => one.kind === 'scene' && String(one.branchId) === String(initialBranchId),
      );
      if (block) {
        caret.goToLine(Number(block.line));
      }
    }
  }, [caret, initialBranchId, map, show, text]);

  // The popover's parent is the modal dialog, not the body: the dialog is the Modal's own stacking
  // context and focus trap. If the dialog cannot be found, the body still works.
  useEffect(() => {
    if (!show) {
      setPopoverContainer(null);

      return;
    }
    const dialog = bodyRef.current ? bodyRef.current.closest('.modal-dialog') : null;
    setPopoverContainer(dialog ?? window.document.body);
  }, [show]);

  /**
   * The page behind the dialog does not scroll while Basil is open.
   *
   * The surface is full-bleed and every scroller on it is internal — the script, the rail's three
   * sections, the drawer — so a page scrolling behind it would only move the caret box the popover
   * is placed from. The inline value that was there before is put back on close, so nothing about
   * the page's own scrolling is decided here.
   */
  useEffect(() => {
    if (!show) {
      return undefined;
    }
    const { body } = window.document;
    const previous = body.style.overflow;

    body.style.overflow = 'hidden';

    return () => {
      body.style.overflow = previous;
    };
  }, [show]);

  /**
   * S-2: the exit notice, held through a ref.
   *
   * Every way out of the editor is written above the state that decides whether there is anything
   * to say, so the three exits call this and {@link sayOnExit} fills it in below. It is never null,
   * and on an editor with nothing unaccounted for it does nothing at all.
   */
  const sayOnExitRef = useRef<() => void>(() => undefined);

  /**
   * Close, the dialog's own escape and its backdrop all arrive here.
   *
   * While a batch is out none of them closes anything: the editor is the only place the writer's
   * script exists, and an unmount during the interval would lose it whichever way the batch ends.
   */
  const requestClose = useCallback(() => {
    if (applyInFlight) {
      showToast(APPLY_WAIT_TO_CLOSE);

      return;
    }
    if (isDirty) {
      setPendingNavigateId(null);
      setPendingNavigateName(null);
      setShowCloseConfirm(true);

      return;
    }
    sayOnExitRef.current();
    onHide();
  }, [applyInFlight, isDirty, onHide, showToast]);

  /**
   * Every preview row's click arrives here (BAR-SHIP2-04).
   *
   * The mount's navigation callback hides the modal and scrolls the canvas, so a row was a second
   * way out of the editor: it passed neither the in-flight lock nor the dirty-close confirmation,
   * and a click on it discarded unsent text or unmounted the surface a batch was still being read
   * back into. It is now the same wall Close meets — refuse while a batch is out, ask before
   * discarding a dirty script, and navigate only when the editor is clean and idle.
   */
  const requestNavigate = useCallback((nodeId: number) => {
    if (!onNavigateToNode) {
      return;
    }
    if (applyInFlight) {
      showToast(APPLY_WAIT_TO_CLOSE);

      return;
    }
    if (isDirty) {
      setPendingNavigateId(nodeId);
      // P3: the question names the scene it would open, so the writer knows where they are going
      // before they agree to lose anything.
      setPendingNavigateName(sceneOfBranch(headingsRef.current, graphRef.current, nodeId));
      setShowCloseConfirm(true);

      return;
    }
    sayOnExitRef.current();
    onNavigateToNode(nodeId);
  }, [applyInFlight, isDirty, onNavigateToNode, showToast]);

  /**
   * "Close without saving", on the dirty-close confirmation.
   *
   * That dialog can already be STANDING when a batch goes out — Ctrl+Enter reaches the
   * document-level handler whatever is on top of the surface — and its own confirm used to call
   * `onHide` directly, which is a door around {@link requestClose} and out of the interval. It goes
   * through the same wall as every other way out, so there is no arrangement of dialogs in which the
   * editor can be unmounted while a batch is still being sent and read back.
   *
   * It is also the only path that carries out a held preview navigation: the writer discarding the
   * script here is the one moment a row's click may leave the editor.
   */
  const confirmCloseWithoutSaving = useCallback(() => {
    if (applyInFlight) {
      showToast(APPLY_WAIT_TO_CLOSE);

      return;
    }
    setShowCloseConfirm(false);
    sayOnExitRef.current();

    if (pendingNavigateId != null && onNavigateToNode) {
      setPendingNavigateId(null);
      setPendingNavigateName(null);
      onNavigateToNode(pendingNavigateId);

      return;
    }
    onHide();
  }, [applyInFlight, onHide, onNavigateToNode, pendingNavigateId, showToast]);

  /** "Stay", on the dirty-close confirmation: the script stands and any held navigation is dropped. */
  const cancelCloseConfirm = useCallback(() => {
    setShowCloseConfirm(false);
    setPendingNavigateId(null);
    setPendingNavigateName(null);
  }, []);

  // The live hook, read through a ref so the handlers below keep a stable identity.
  const applyRef = useRef(apply);

  applyRef.current = apply;

  /**
   * The change set the confirmation is about, captured the moment the dialog OPENS (BAR-SHIP-09).
   *
   * The debounced analysis keeps running while the dialog is up; reading the plans when the operator
   * confirms could send a set nobody reviewed. What is frozen here is what runs, or nothing does.
   */
  const [frozen, setFrozen] = useState<FrozenApplyArgs | null>(null);
  /**
   * The exact script revision the confirmation was made from (BAR-SHIP-14, J2).
   *
   * The frozen plans say WHICH changes go out. This says which text they were the changes to, and it
   * is what the success close is bound to: `onSave` is the mount's close-and-reload, so it may only
   * run while the script in front of the writer is still that same revision.
   */
  const [confirmedRevision, setConfirmedRevision] = useState<string | null>(null);
  /** The writer-facing reason the last apply did not finish. Null while nothing has gone wrong. */
  const [applyFailure, setApplyFailure] = useState<string | null>(null);
  // T23: one settlement at a time from this control. The click is ignored and the button disabled
  // while an attestation is being written, so a double press cannot admit two settlements.
  const [settling, setSettling] = useState(false);
  const settlingRef = useRef(false);
  /** P4: where every request of the last apply ended up. Null before the first apply resolves. */
  const [applyOutcome, setApplyOutcome] = useState<ApplyOutcome | null>(null);
  /**
   * T17-02 and T18-01: the nodes an earlier attempt in this session could not account for.
   *
   * It is a fact about the EPISODE and not about the attempt that discovered it, so it survives
   * every later attempt, every cancelled confirmation and every later success. It is kept as the
   * NODES rather than as a flag, because that is what lets a later attempt settle the change it
   * actually confirmed and nothing else (`settlementOf`, the settled rule).
   */
  const [unsettledNodes, setUnsettledNodes] = useState<string[]>([]);
  /** The same set, readable from the apply handler without re-binding it on every change. */
  const unsettledRef = useRef<string[]>(unsettledNodes);

  unsettledRef.current = unsettledNodes;
  /**
   * S-3: what the DURABLE log is still waiting on for this episode, from earlier sessions.
   *
   * The nodes above are this session's memory and die with the page; the log does not. An unknown
   * outcome from yesterday refuses every apply in this browser until a writer attests to it, so the
   * warning and the control that carries the attestation have to be reachable without spending an
   * apply on the refusal first.
   *
   * THE READ POINT IS THE OPEN, not the first refusal. It is the simpler of the two the brief
   * allows: one effect, one read, no new path from the controller's refusal back to the surface,
   * and the writer meets the warning at the moment they can still act on it.
   */
  const [earlierRecords, setEarlierRecords] = useState(0);
  /** The same count, readable from the open read without re-binding the effect on every change. */
  const earlierRecordsRef = useRef(earlierRecords);

  earlierRecordsRef.current = earlierRecords;
  /** True while any earlier change is still unaccounted for, in this session or in the log. */
  const earlierUnconfirmed = unsettledNodes.length > 0 || earlierRecords > 0;
  /** Set when a batch came back good over a script that has since moved on. */
  const [newerEditNote, setNewerEditNote] = useState<string | null>(null);

  // S-3: ask the durable log, once, when the editor opens. A log that will not answer is left to
  // the apply guard, which refuses on its own terms; nothing here invents a warning out of it.
  useEffect(() => {
    if (!show) {
      return undefined;
    }
    let live = true;

    applyRef.current.countEarlierUnresolved()
      .then((count) => {
        // The log answering what the surface already holds — nothing, in the ordinary case —
        // changes nothing, so it is not written back.
        if (live && count !== earlierRecordsRef.current) {
          setEarlierRecords(count);
        }
      })
      .catch(() => undefined);

    return () => {
      live = false;
    };
  }, [show]);

  /**
   * Apply is live only for a current analysis in which every single row can be sent — and never
   * while a batch is already out, because concurrency is one and a second confirmation would be
   * about a revision the first one is still deciding.
   */
  const canApplyNow = apply.canApply && everyRowSendable && !applyInFlight;

  /**
   * T18-01: what one resolved attempt settles, and what it leaves unsettled.
   *
   * It is applied to the ref as well as to the state, because the success path has to know, in the
   * same tick, whether anything is still unaccounted for: that decides the sentence the story view
   * is given and whether the editor may close over the warning at all.
   */
  const recordSettlement = useCallback((state: ApplyState | null, plans: Plan[]): string[] => {
    const remaining = nextUnsettled(unsettledRef.current, state, plans);

    unsettledRef.current = remaining;
    setUnsettledNodes(remaining);

    return remaining;
  }, []);

  /**
   * T19-02: what this editor last told the story view, so it can be taken back when it goes stale.
   *
   * The notice outlives the editor by design, which is exactly why it has to be kept honest. It is
   * held here as its two parts rather than as the one string it was sent as: the sentence about the
   * attempt, which stays true whatever happens next, and whether the warning about earlier changes
   * was said beside it, which does not.
   */
  const storyNoticeRef = useRef<{ said: string; earlier: boolean } | null>(null);

  /**
   * Say something to the story view, and remember what was said.
   *
   * The warning is appended here and nowhere else, so there is one place that knows whether the
   * standing notice carries it.
   */
  const sayToStory = useCallback((said: string | null, remaining: string[]): void => {
    if (!onNotice || !said) {
      return;
    }
    const earlier = remaining.length > 0;

    storyNoticeRef.current = { said, earlier };
    onNotice(earlier ? `${said} ${EARLIER_UNCONFIRMED}` : said);
  }, [onNotice]);

  /**
   * S-2: what the story view is told when the writer LEAVES with something unaccounted for.
   *
   * The durable record survives the close, and reopening Basil shows the whole recovery block
   * again, so nothing is lost. What was missing was any sign of it on the page the writer goes back
   * to: only a verified success emitted a notice, and the story view keeps a notice until it is
   * dismissed, so a writer who succeeded once and then closed over an unresolved record was left
   * reading a green sentence about a change that was applied and checked.
   *
   * It goes through the SAME `onNotice` path as every other sentence, so it replaces whatever was
   * standing there, and it is recorded in `storyNoticeRef` as a notice that carries the warning, so
   * the settlement path above knows to take it back rather than repeat it.
   */
  const sayOnExit = useCallback(() => {
    if (!onNotice || (unsettledRef.current.length === 0 && earlierRecordsRef.current === 0)) {
      return;
    }
    storyNoticeRef.current = { said: EXIT_UNCONFIRMED, earlier: true };
    onNotice(EXIT_UNCONFIRMED);
  }, [onNotice]);

  sayOnExitRef.current = sayOnExit;

  /**
   * T18-01: the writer's own statement that they went and looked. The only thing they can do that
   * settles an earlier unknown, and it settles all of it, because that is what they are saying.
   *
   * T19-02: and it settles it everywhere it was said. The story view is holding a notice this
   * editor wrote, and half of that notice is now untrue: the writer has just answered the very
   * question it asks. It is rewritten to the attempt's own result alone, which is the part of it
   * that is still a fact. The notice stays, and stays dismissible; only the warning goes.
   */
  const dismissEarlierWarning = useCallback(() => {
    if (settlingRef.current) {
      return;
    }
    settlingRef.current = true;
    setSettling(true);
    // S-3: the attestation goes to the LOG first, and the in-memory state is cleared only once it
    // is durable. Clearing the screen over a write that did not land is how the writer was told the
    // episode was fine while the next apply was still refused.
    applyRef.current.settleEarlier()
      .finally(() => {
        settlingRef.current = false;
        setSettling(false);
      })
      .then(({ remaining }) => {
        setEarlierRecords(remaining);

        if (remaining > 0) {
          // Something in the log cannot be attested to — a pre-write entry whose request was never
          // recorded. The warning stays, because the guard will still raise it.
          return;
        }

        unsettledRef.current = [];
        setUnsettledNodes([]);
        // S-3c: the refusal that named this control is answered by it, so it goes with the control.
        // It used to stay on the screen after the button disappeared, still telling the writer to
        // press a button that was no longer there. Only that refusal is cleared: every other
        // sentence the pipeline filed about the last attempt is still true and stays.
        // T23: and so does the sentence about an earlier answer that did not save, once one has.
        setApplyFailure((failure) => (
          failure && (failure.indexOf(SETTLE_FIRST) !== -1 || failure === ATTESTATION_NOT_SAVED)
            ? null
            : failure
        ));

        if (!onNotice) {
          return;
        }
        const standing = storyNoticeRef.current;

        // T19-02: the notice this editor wrote was half about the attempt and half about the
        // earlier uncertainty. The attempt's own result is still a fact, so it is said again on its
        // own and only the warning goes.
        if (standing && standing.earlier && standing.said !== EXIT_UNCONFIRMED) {
          storyNoticeRef.current = { said: standing.said, earlier: false };
          onNotice(standing.said);

          return;
        }

        // S-2: there is nothing left of it to say. Either the whole notice WAS the warning this
        // editor wrote on the way out, or the warning was written by an earlier visit and this
        // editor has said nothing yet. Both are answered now, so the story view is cleared.
        if (!standing || standing.earlier) {
          storyNoticeRef.current = null;
          onNotice('');
        }
      })
      .catch(() => {
        // S-3c: the log would not take the attestation, or an apply was holding the write lane.
        // Nothing is settled and the warning stays exactly where it was. The sentence is about the
        // ANSWER that did not save: the old one said "nothing was sent", which is a sentence about
        // a request and told the writer the wrong thing about an attempt they never made.
        setApplyFailure(ATTESTATION_NOT_SAVED);
      });
  }, [onNotice]);

  /**
   * T18-01: opening the confirmation clears THIS attempt's view and nothing else.
   *
   * The earlier uncertainty is not part of the attempt being replaced, so it is not cleared with
   * it: the warning and the record download stay where they are while the question is on the
   * screen and after "Not yet" closes it again.
   */
  const openApplyConfirm = useCallback(() => {
    setFrozen(applyRef.current.freeze());
    setConfirmedRevision(analysedRevisionRef.current);
    setApplyFailure(null);
    setNewerEditNote(null);
    setApplyOutcome(null);
    setShowApplyConfirm(true);
  }, []);

  // Ctrl/Cmd+Enter opens the apply confirmation; it never applies directly, and there is no Ctrl+S.
  const canApplyRef = useRef(canApplyNow);

  canApplyRef.current = canApplyNow;
  useEffect(() => {
    if (!show) {
      return undefined;
    }
    const handler = (event: KeyboardEvent) => {
      if (event.key === 'Enter' && (event.ctrlKey || event.metaKey) && canApplyRef.current) {
        event.preventDefault();
        event.stopPropagation();
        openApplyConfirm();
      }
    };
    window.document.addEventListener('keydown', handler, true);

    return () => window.document.removeEventListener('keydown', handler, true);
  }, [openApplyConfirm, show]);

  /**
   * Run the frozen set, and close ONLY on a verified success of the revision that was confirmed.
   *
   * `onSave` is the mount's close-and-reload: it unmounts the editor and discards whatever is in
   * the textarea. Every other resolved outcome of the pipeline — a stale baseline, an unavailable
   * write log, counts that did not match, a rejected verdict, an outcome nobody observed — leaves
   * the modal open, the script exactly as the writer left it, and the dirty-close confirmation
   * still in the way of losing it.
   *
   * Two walls stand between a good batch and a lost line (BAR-SHIP-14):
   *
   * 1. the interval is **locked** — `applyInFlight` shuts every editing entry point from here until
   *    the pipeline's final state, so no revision newer than the confirmed one can be made;
   * 2. the close is **bound** — even so, `onSave` runs only while the script still equals the
   *    revision the confirmation captured. If it does not, the batch is reported as applied and the
   *    newer text stays exactly where the writer left it.
   */
  const confirmApply = useCallback(() => {
    setShowApplyConfirm(false);

    if (!frozen || frozen.plans.length === 0) {
      return;
    }

    const revision = confirmedRevision;

    setApplyFailure(null);
    setNewerEditNote(null);
    setApplyOutcome(null);
    setApplyInFlight(true);

    applyRef.current.apply(frozen)
      .then((final) => {
        setApplyInFlight(false);

        if (!isVerifiedSuccess(final)) {
          // P4: the recovery view names every outcome with its actual count, and it stays until the
          // writer chooses to leave. The refusal's own sentence comes with it, never instead of it.
          const ended = outcomeOf(final);

          setApplyOutcome(ended);
          recordSettlement(final, frozen.plans);
          setApplyFailure(
            refusalSaid(final)
              || (final.refusal ? BASIL_REFUSAL_COPY[final.refusal] : null)
              || APPLY_NOT_APPLIED,
          );

          return;
        }

        setApplyFailure(null);
        // P4: the confirmed sentence is the ONLY one allowed after confirmation, and it outlives
        // the modal: the mount keeps it on the story view until the writer dismisses it.
        const confirmed = outcomeOf(final);

        setApplyOutcome(confirmed);

        // T18-01: this attempt settles the nodes it actually re-read and verified. Anything left is
        // an earlier change nobody can account for, and it is said beside this attempt's success.
        const remaining = recordSettlement(final, frozen.plans);

        sayToStory(outcomeSentence(confirmed), remaining);

        if (revision != null && textRef.current !== revision) {
          setNewerEditNote(NEWER_EDIT_KEPT);

          return;
        }

        if (remaining.length > 0) {
          // T18-01: a success does not close the editor over an unread warning. The writer leaves
          // when they choose to, by the protected exit, with the record still downloadable.
          return;
        }

        // P1: the instruction stands through the writer's FIRST CONFIRMED apply in this episode,
        // and this is that moment. It is recorded before the close, because the close unmounts.
        writeIntroductionDone(episodeId);
        setIntroductionDone(true);

        if (onSave) {
          onSave();
        }
      })
      .catch(() => {
        setApplyInFlight(false);
        setApplyOutcome(outcomeOf(applyRef.current.state));
        recordSettlement(applyRef.current.state, frozen.plans);
        setApplyFailure(APPLY_NOT_APPLIED);
      });
  }, [confirmedRevision, episodeId, frozen, onSave, recordSettlement, sayToStory]);

  /** The write log, as it stands, for the record. Offered only where recovery needs it. */
  const exportWriteLog = useCallback(() => {
    applyRef.current.exportAudit()
      .then((blob) => {
        const anchor = window.document.createElement('a');
        const href = window.URL.createObjectURL(blob);

        anchor.href = href;
        anchor.download = `basil-write-log-${String(episodeId)}.jsonl`;
        anchor.click();
        window.URL.revokeObjectURL(href);
        showToast('Downloaded.');
      })
      .catch(() => showToast("The download didn't work. Try again."));
  }, [episodeId, showToast]);

  // Where the write lane has got to, read by the progress sentences, the plan rows and the outcome.
  const applyState = apply.state;

  /**
   * What the bar says while a batch is out (P4).
   *
   * "Applying 1 change..." is the SEND; "Checking the change..." is the verification that follows
   * it. Neither is a success and neither claims to be: the verified sentence is written in exactly
   * one place, after the pipeline's own confirmation.
   */
  const inFlightCount = frozen ? frozen.plans.length : 0;
  let progressNote = applyingSentence(inFlightCount);

  if (applyState.phase === 'rereading' || applyState.phase === 'verifying') {
    progressNote = checkingSentence(inFlightCount);
  } else if (applyState.phase === 'idle' || applyState.phase === 'preflight') {
    // T17-08: nothing has been dispatched yet, and a run that stops here never dispatches anything.
    progressNote = PREPARING_CHANGES;
  }

  /**
   * The outcome line the recovery view leads with, and whether anything is still unconfirmed.
   *
   * `outcomeSentence` is null only for an apply with no requests in it at all — a refusal before
   * the preflight ever built a status. There is then nothing to count, and the refusal's own
   * sentence stands alone.
   */
  const recovery = useMemo(
    () => recoveryLines({ outcome: applyOutcome, earlierUnconfirmed, detail: applyFailure }),
    [applyFailure, applyOutcome, earlierUnconfirmed],
  );

  // A refusal never reaches a writer as the write lane's own code word.
  const refusalCopy = apply.reason ? (BASIL_REFUSAL_COPY[apply.reason] ?? null) : null;
  const sendabilityNote = sendabilitySentence(sendability);

  /**
   * Why Apply is not live, in one sentence, on the control and beside it.
   *
   * A durable refusal — no verifier, no write log, the gate — outranks "still working": it is the
   * fact that will still be true when the analysis lands.
   */
  let applyBlockedNote: string | null = null;

  if (applyInFlight) {
    // A batch that is out outranks every other sentence: it is the reason the script is locked, and
    // it is the only one that will stop being true on its own. P4 splits the interval in two, so
    // the writer can tell the sending from the checking — and neither of them is success.
    applyBlockedNote = progressNote;
  } else if (refusalCopy && apply.reason !== 'nothing-to-apply') {
    applyBlockedNote = refusalCopy;
  } else if (analysisError) {
    applyBlockedNote = ANALYSIS_FAILED;
  } else if (isAnalysing || (analysis != null && analysis.analysedText !== text)) {
    applyBlockedNote = WORKING_ON_THE_LATEST_EDIT;
  } else if (sendabilityNote) {
    applyBlockedNote = sendabilityNote;
  } else {
    applyBlockedNote = refusalCopy;
  }
  const findings = useMemo(() => (analysis ? analysis.findings : NO_FINDINGS), [analysis]);
  const changeCount = analysis ? analysis.changeCount : 0;
  const rows = useMemo(() => (analysis ? analysis.rows : []), [analysis]);

  let statusWord = 'reading…';
  let statusState: BasilStatusState = 'working';
  if (read.requestState === 'error' || basilDocument.errorMessage) {
    statusWord = "couldn't open the episode";
    statusState = 'error';
  } else if (read.requestState === 'success' && !isAnalysing && analysisError) {
    statusWord = "can't follow the script yet";
    statusState = 'error';
  } else if (read.requestState === 'success' && isAnalysing) {
    statusWord = 'working…';
  } else if (read.requestState === 'success' && findings.length > 0) {
    statusWord = `${findings.length} to look at`;
    statusState = 'findings';
  } else if (read.requestState === 'success') {
    statusWord = 'ready';
    statusState = 'ok';
  }

  /**
   * The suggestions actually shown (P6).
   *
   * A scene with no link onward is a neutral observation, not a problem the writer caused, and it
   * is cut from the FIRST OPEN — before the writer has typed anything. Every genuinely blocking
   * problem stays visible at every moment, because those are about something being wrong.
   */
  const shownFindings = useMemo(
    () => (isDirty ? findings : findings.filter((finding) => NEUTRAL_RULES.indexOf(finding.rule) === -1)),
    [findings, isDirty],
  );

  /**
   * T17-06: what the writer must see, and what they may look at when they choose to.
   *
   * A blocker is a finding the rules call an error and that is not one of the neutral observations.
   * Keeping it in the document is not keeping it visible: the Suggestions fold is closed on open, so
   * a blocker inside it is a problem a writer is never told about. Blockers are shown outside the
   * fold; only optional suggestions collapse.
   */
  const blockingFindings = useMemo(
    () => shownFindings.filter(
      (finding) => finding.severity === 'error' && NEUTRAL_RULES.indexOf(finding.rule) === -1,
    ),
    [shownFindings],
  );
  const optionalFindings = useMemo(
    () => shownFindings.filter(
      (finding) => !(finding.severity === 'error' && NEUTRAL_RULES.indexOf(finding.rule) === -1),
    ),
    [shownFindings],
  );

  /**
   * T17-06: the missing-name guidance, keyed by the line of the label it is about.
   *
   * It is rendered in the margin, beside that label, rather than in the collapsed panel: the note
   * tells the writer to leave a label alone, and the label is on the screen.
   */
  const nameNotesByLine = useMemo(() => {
    const byLine = new Map<number, NameGap[]>();

    nameGaps.notes.forEach((note) => {
      byLine.set(note.line, [...(byLine.get(note.line) ?? []), note]);
    });

    return byLine;
  }, [nameGaps]);

  const sceneNameForLine = useCallback((line: number | null) => {
    if (line == null) {
      return null;
    }

    return sceneAt(text, line).name;
  }, [text]);

  // The plan panel shows where each request actually stands: `planned` until the write lane takes
  // it, then the write lane's own word for it — queued, sending, reconciled, mismatched, verified or
  // rejected (spec section 4.11). The two vocabularies never mix: a structural classification keeps
  // its own row.
  const planRows = useMemo(() => rows.map((row) => {
    const live = row.planId ? applyState.statuses[row.planId] : undefined;

    if (!live) {
      return row;
    }

    return {
      ...row,
      status: live.status,
      message: live.message ?? row.message,
      chunkIndex: live.chunkIndex,
    };
  }), [applyState, rows]);

  // An engine warning goes under the request whose node it names, verbatim, field and message.
  const warningsByRow = useMemo(() => {
    const byRow: Record<string, PlanWarning[]> = {};
    (applyState.warnings ?? []).forEach((warning) => {
      const owner = planRows.find((row) => row.branchId != null && row.branchId === String(warning.nodeId));
      const key = owner ? owner.key : '';

      byRow[key] = [...(byRow[key] ?? []), warning];
    });

    return byRow;
  }, [applyState, planRows]);

  // The `before` is the projection of the read the script opened with, taken by the very function
  // that produced the `after`. Passing `null` here meant `PreviewPanel` could never mark a scene
  // changed, because a row is changed only when the same id carries a different line in the before
  // map (spec §4.10, the before/after requirement).
  const preview = useMemo(
    () => previewRows(
      analysis ? analysis.projection : null,
      analysis ? analysis.beforeProjection : null,
      graph,
      headings,
    ),
    [analysis, graph, headings],
  );

  // The dragged width reaches the grid as a custom property, so the handle repaints one column
  // rather than handing every pointer move down to the script.
  const railStyle = useMemo(
    () => ({ '--basil-rail-width': `${railWidth}px` } as React.CSSProperties),
    [railWidth],
  );

  /**
   * P6: Suggestions is COLLAPSED by default, and its count is calculated.
   *
   * A writer opening their own episode is not given a list of things to look at before they have
   * looked at anything. The count is the number actually shown — the neutral rule is cut from first
   * open, and a count that included it would be counting something the panel is not showing.
   */
  const adviceSection = (
    <div
      className={css.railSection}
      key="advice"
      data-testid="basil-rail-section"
      data-tab="suggestions"
      data-active={railTab === 'suggestions'}
    >
      <details
        className={css.railFold}
        open={suggestionsOpen}
        data-testid="basil-suggestions"
        onToggle={(event) => setSuggestionsOpen((event.currentTarget as HTMLDetailsElement).open)}
      >
        <summary className={css.railHeading}>
          {`${SUGGESTIONS} · ${suggestionCount(optionalFindings.length)}`}
        </summary>
        <div className={css.railBody}>
          <AdvicePanel
            findings={optionalFindings}
            unnamedVariables={nameGaps.unnamed}
            sceneNameForLine={sceneNameForLine}
            onApply={handleApplySuggestion}
            onGoToLine={handleGoToLine}
            isReadOnly={editingLocked}
          />
        </div>
      </details>
    </div>
  );
  const previewSection = (
    <div
      className={css.railSection}
      key="preview"
      data-testid="basil-rail-section"
      data-tab="scenes"
      data-active={railTab === 'scenes'}
    >
      <h2 className={css.railHeading}>Scenes</h2>
      <div className={css.railBody}>
        <PreviewPanel rows={preview} onNavigateToNode={requestNavigate} />
      </div>
    </div>
  );
  // What would be sent leads the rail the moment there is anything to send, and carries the band
  // that says so; with nothing staged it goes back under the two panels a writer reads while typing.
  const planSection = (
    <div
      className={css.railSection}
      key="plan"
      data-emphasis={changeCount > 0}
      data-testid="basil-rail-section"
      data-tab="changes"
      data-active={railTab === 'changes'}
    >
      <h2 className={css.railHeading}>Changes</h2>
      <div className={css.railBody}>
        <PlanPanel
          rows={planRows}
          warningsByRow={warningsByRow}
          details={applyState.details}
          onOpenScene={requestNavigate}
        />
      </div>
    </div>
  );
  /**
   * T17-06, corrected by T18-03: the problems a writer must see, outside every fold and every tab.
   *
   * Putting them outside the Suggestions disclosure was not enough. Under 1080 px the rail shows
   * one panel at a time, and the panel it opens on is Changes, so a blocker inside the Suggestions
   * section was `display: none` until the writer picked a tab they had no reason to pick. This
   * strip is not in the rail at all: it is a band across the surface, above the workspace, and the
   * blocking sentence is readable there at every width without a click.
   */
  const attentionStrip = blockingFindings.length > 0 ? (
    <div className={css.attention} data-testid="basil-blockers">
      <h2 className={css.attentionHeading}>{NEEDS_ATTENTION}</h2>
      <AdvicePanel
        findings={blockingFindings}
        label={NEEDS_ATTENTION}
        rowWord="Problem"
        showFixedSentence={false}
        showAllClear={false}
        unnamedVariables={[]}
        sceneNameForLine={sceneNameForLine}
        onApply={handleApplySuggestion}
        onGoToLine={handleGoToLine}
        isReadOnly={editingLocked}
      />
    </div>
  ) : null;
  /**
   * T17-06, given room by T18-04: the guidance for names the script cannot spell.
   *
   * It sits in a panel of its own, in the flow above the workspace, because the margin positions
   * each row's box against the height of a script line: a paragraph in there hangs down over the
   * rows below it, and two notes on neighbouring lines covered each other. The margin keeps the
   * one-line marker that says which label each note is about.
   */
  const nameNotesPanel = (
    <NameNotes
      notes={nameGaps.notes}
      onGoToLine={handleGoToLine}
      onOpenScene={requestNavigate}
    />
  );
  /**
   * T19-04: every label on one line, where there is room for all of them.
   *
   * The margin is one row per script line and each row is as tall as its line, so a row that holds
   * more than a line's worth would hold it over the next row. It keeps what fits and counts the
   * rest; this is where the rest is, in the flow above the script, on the writer's word.
   */
  const mediaDetailLabels = mediaDetailLine != null ? (mediaLabels.get(mediaDetailLine) ?? []) : [];
  const mediaDetailPanel = mediaDetailLine != null && mediaDetailLabels.length > 0 ? (
    <MediaDetail
      line={mediaDetailLine}
      labels={mediaDetailLabels}
      onOpenScene={requestNavigate}
      onDismiss={() => setMediaDetailLine(null)}
    />
  ) : null;
  const railSections = changeCount > 0
    ? [planSection, adviceSection, previewSection]
    : [adviceSection, previewSection, planSection];

  return (
    <Modal
      show={show}
      onHide={requestClose}
      aria-labelledby="basil-title"
      dialogClassName={css.fullBleed}
    >
      <div
        className={css.shell}
        ref={bodyRef}
        data-testid="basil-shell"
        data-narrow={narrowLayout}
        style={heightBudgetStyle()}
      >
        <header className={css.header}>
          <h1 className={css.brand} id="basil-title">Basil</h1>
          <div className={css.headerStatus}>
            <StatusBar
              title={titleOf(text, story)}
              changeCount={changeCount}
              statusWord={statusWord}
              statusState={statusState}
              contextOpen={contextOpen}
              onToggleContext={() => setContextOpen((open) => !open)}
              isReadOnly={isReadOnly}
              sendabilityNote={sendabilityNote}
            />
          </div>
          <div className={css.headerActions}>
            <div className={css.actionNotes}>
              {applyBlockedNote ? (
                <p className={css.refusal} role="status" data-testid="basil-apply-note">{applyBlockedNote}</p>
              ) : null}
              <p className={css.hint}>{KEY_HINT}</p>
            </div>
            {!isReadOnly ? (
              <Button
                variant="primary"
                disabled={!canApplyNow}
                title={applyBlockedNote ?? 'Ctrl+Enter'}
                onClick={openApplyConfirm}
              >
                Apply changes
              </Button>
            ) : null}
            <Button
              variant="outline-secondary"
              aria-disabled={applyInFlight}
              title={applyInFlight ? APPLY_WAIT_TO_CLOSE : undefined}
              onClick={requestClose}
            >
              Close
            </Button>
          </div>
        </header>
        {applyFailure || earlierUnconfirmed || newerEditNote ? (
          <div className={css.notices} data-testid="basil-notices">
            {applyFailure || earlierUnconfirmed ? (
              <div className={css.refusal} role="status" data-testid="basil-apply-recovery">
                {/* T22: the controls come FIRST, above the sentences.
                    The block is bounded now and scrolls inside itself, so whatever is at the top of
                    it is the part that is always on the screen. The two things the writer can
                    actually DO must be that part: the longest recovery state ran to every sentence
                    plus both buttons, and the buttons were under all of it. */}
                <div className={css.recoveryActions} data-testid="basil-recovery-actions">
                  <button type="button" onClick={exportWriteLog}>Download the change record</button>
                  {earlierUnconfirmed ? (
                    <button
                      type="button"
                      data-testid="basil-settle-earlier"
                      disabled={settling}
                      onClick={dismissEarlierWarning}
                    >
                      {CHECKED_THE_EPISODE}
                    </button>
                  ) : null}
                </div>
                {/* P4 and T17-01: every paragraph here is rendered from the delivery and outcome
                    facts by one function, so no two of them can disagree about whether a change
                    reached the episode. A mixed result keeps its three clauses and its three real
                    numbers, and nothing here ever stands for the whole.
                    T18-01: the block stands on the earlier uncertainty alone, so cancelling the
                    confirmation cannot take the warning and the record away with it. */}
                {recovery.map((line, index) => (
                  <p key={line} data-testid={index === 0 ? 'basil-outcome' : undefined}>{line}</p>
                ))}
              </div>
            ) : null}
            {newerEditNote ? (
              <p className={css.refusal} role="status" data-testid="basil-newer-edit">{newerEditNote}</p>
            ) : null}
          </div>
        ) : null}
        {/* T19-03: the guidance is budgeted as one thing. Two bands that each knew only their own
            share came to 44vh of a 512px window between them, on top of a rail floor and the
            editor's floor, and the editor lost its bottom edge to the shell's hidden overflow. */}
        {blockingFindings.length > 0 || nameGaps.notes.length > 0 || mediaDetailPanel != null ? (
          <div className={css.guidance} data-testid="basil-guidance">
            {attentionStrip}
            {nameNotesPanel}
            {mediaDetailPanel}
          </div>
        ) : null}
        <div
          className={css.workspace}
          data-testid="basil-workspace"
          data-context-open={contextOpen}
          style={railStyle}
        >
          <main className={css.scriptPane} aria-label="Script" data-card-open={exampleOpen}>
            <Introduction
              introductionDone={introductionDone}
              exitExplanation={mediaLabels.size > 0 ? EXIT_EXPLANATION : null}
              onShowExample={showExample}
            />
            {exampleOpen ? (
              <ExplanationCard forms={forms} onDismiss={dismissExample} />
            ) : null}
            <ScriptEditor
              value={text}
              lines={lines}
              markedLines={markedLines}
              currentLine={caret.currentLine}
              exampleLine={exampleLine}
              mediaLabels={mediaLabels}
              nameNotes={nameNotesByLine}
              onOpenScene={requestNavigate}
              onShowLine={setMediaDetailLine}
              readOnly={editingLocked}
              textareaRef={textareaRef}
              mirrorRef={mirrorRef}
              onChange={handleChange}
              onCaretMove={handleCaretMove}
              onKeyDown={autocomplete.handleKeyDown}
              onGoToLine={handleGoToLine}
              onScrollSync={measureCaret}
              onMetrics={handleMetrics}
              listboxId={LISTBOX_ID}
              isListOpen={autocomplete.session != null}
              activeOptionId={autocomplete.session ? `${LISTBOX_ID}-item-${autocomplete.session.active}` : undefined}
            />
          </main>
          {contextOpen ? (
            <button
              type="button"
              className={css.divider}
              aria-label="Resize the side panel"
              onPointerDown={startRailResize}
              onPointerMove={moveRailResize}
              onPointerUp={endRailResize}
              onPointerCancel={endRailResize}
              onKeyDown={nudgeRail}
            />
          ) : null}
          {contextOpen ? (
            <aside className={css.rail} aria-label="Side panel">
              {read.errorMessage || basilDocument.errorMessage ? (
                <p className={css.readError}>{read.errorMessage ?? basilDocument.errorMessage}</p>
              ) : null}
              {/* P6: under the width the two columns were drawn for, one panel at a time. The tabs
                  are in the document at every width and shown only where they are needed. */}
              <div className={css.railTabs} role="tablist" aria-label="Side panel sections">
                {RAIL_TABS.map((tab) => (
                  <button
                    type="button"
                    role="tab"
                    key={tab.id}
                    className={css.railTab}
                    aria-selected={railTab === tab.id}
                    onClick={() => setRailTab(tab.id)}
                  >
                    {tab.word}
                  </button>
                ))}
              </div>
              {railSections}
            </aside>
          ) : null}
        </div>
        {/* P6: the support drawer leaves the writing area. Nothing behind it changed. */}
        <div className={css.drawer}>
          <AdvancedFooter
            rows={planRows}
            raw={analysis ? { requests: analysis.requests, structural: analysis.structural } : null}
            projection={analysis ? analysis.projection : null}
          />
        </div>
        {toast ? <p className={css.toast} role="status">{toast}</p> : null}
      </div>
      <AutocompletePopover
        items={autocomplete.session ? autocomplete.session.items : []}
        active={autocomplete.session ? autocomplete.session.active : 0}
        point={caretPoint}
        onAccept={autocomplete.accept}
        container={popoverContainer}
        listboxId={LISTBOX_ID}
      />
      <ConfirmModal
        centered
        show={showCloseConfirm}
        isLoading={false}
        title={DIRTY_EXIT_TITLE}
        confirmButtonTitle={discardAndOpen(pendingNavigateName)}
        cancelButtonTitle={KEEP_WRITING}
        onConfirm={confirmCloseWithoutSaving}
        onCancel={cancelCloseConfirm}
        backdrop="static"
      >
        {DIRTY_EXIT_BODY}
      </ConfirmModal>
      <ConfirmModal
        centered
        show={showApplyConfirm}
        isLoading={apply.state ? apply.state.phase === 'sending' : false}
        title={APPLY_CONFIRM_TITLE}
        confirmButtonTitle={CONFIRM_APPLY}
        cancelButtonTitle={CONFIRM_NOT_YET}
        onConfirm={confirmApply}
        onCancel={() => setShowApplyConfirm(false)}
        backdrop="static"
      >
        {confirmQuestion(frozen ? frozen.plans.length : 0)}
      </ConfirmModal>
    </Modal>
  );
}
